import React from 'react';
import { Wind, Music, Mic2, Clock } from 'lucide-react';
import Icon from '../../../components/AppIcon';


/**
 * Current Activity Display Component
 * Shows current practice segment with tool-specific instructions
 */
export default function CurrentActivityDisplay({ 
  currentSegment, 
  segmentTimeRemaining, 
  sessionStatus 
}) {
  
  const getToolIcon = (toolId) => {
    const icons = {
      'breath-coach': Wind,
      'pitch-coach': Music,
      'audition-songs': Mic2
    };
    return icons?.[toolId] || Music;
  };

  const getToolColor = (toolId) => {
    const colors = {
      'breath-coach': 'from-cyan-400 to-blue-500',
      'pitch-coach': 'from-purple-400 to-pink-500',
      'audition-songs': 'from-orange-400 to-red-500'
    };
    return colors?.[toolId] || 'from-gray-400 to-gray-600';
  };

  const getToolInstructions = (segment) => {
    if (segment?.tool === 'breath-coach') {
      return {
        title: '🫁 Breath Coach',
        subtitle: segment?.config?.preset || 'Box Breathing Pattern',
        instructions: [
          'Find a comfortable seated position',
          'Close your eyes and focus on your breathing',
          'Follow the breathing pattern shown',
          'Stay relaxed and centered'
        ]
      };
    } else if (segment?.tool === 'pitch-coach') {
      return {
        title: '🎵 Pitch Coach',
        subtitle: `${segment?.config?.vocalStyle || 'Mix'} - ${segment?.config?.mode || 'Melody Matching'}`,
        instructions: [
          'Warm up your vocal cords',
          'Match the pitch as accurately as possible',
          'Focus on breath support',
          'Listen carefully to the reference tone'
        ]
      };
    } else if (segment?.tool === 'audition-songs') {
      return {
        title: '🎤 Audition Song Practice',
        subtitle: segment?.config?.recommendationType || 'Current Role Focus',
        instructions: [
          'Review the recommended songs',
          'Practice challenging sections',
          'Focus on character interpretation',
          'Work on vocal technique and expression'
        ]
      };
    }
    return {
      title: 'Practice Activity',
      subtitle: 'Follow the instructions',
      instructions: ['Practice your technique', 'Stay focused', 'Take your time']
    };
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const Icon = getToolIcon(currentSegment?.tool);
  const color = getToolColor(currentSegment?.tool);
  const instructions = getToolInstructions(currentSegment);

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-6 border border-white/20">
      <div className="text-center mb-8">
        {/* Tool Icon and Title */}
        <div className={`inline-block p-4 bg-gradient-to-r ${color} rounded-2xl mb-4`}>
          <Icon className="w-12 h-12 text-white" />
        </div>
        
        <h2 className="text-3xl font-bold text-white mb-2">
          {instructions?.title}
        </h2>
        <p className="text-white/80 text-lg mb-6">
          {instructions?.subtitle}
        </p>

        {/* Timer Display */}
        <div className="bg-white/10 rounded-2xl p-6 mb-8 max-w-md mx-auto">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Clock className="w-6 h-6 text-white/60" />
            <span className="text-white/60 text-sm">Time Remaining</span>
          </div>
          <div className="text-6xl font-bold text-white">
            {sessionStatus === 'active' ? formatTime(segmentTimeRemaining) : '--:--'}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white/5 rounded-2xl p-6 max-w-2xl mx-auto">
          <h3 className="text-white font-bold mb-4">Instructions</h3>
          <ul className="space-y-3 text-left">
            {instructions?.instructions?.map((instruction, index) => (
              <li key={index} className="flex items-start gap-3 text-white/80">
                <span className="text-white font-bold">•</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Status Message */}
        {sessionStatus === 'paused' && (
          <div className="mt-6 bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4 text-yellow-200">
            ⏸ Session Paused - Click Resume to continue
          </div>
        )}

        {sessionStatus === 'ready' && (
          <div className="mt-6 bg-green-500/20 border border-green-500/30 rounded-xl p-4 text-green-200">
            ▶ Ready to start! Click the Start button below
          </div>
        )}
      </div>
    </div>
  );
}